import { Handle, Position } from '@xyflow/react';
import { HourglassIcon, TriangleAlertIcon } from 'lucide-react';
import { type FlowNodeData } from '../../../types/node-data';
import { formatDuration } from '../../../utils/format';
import { Action } from './action';

const getBadgeStyle = (
  resourceType: string,
  flowType: string
): {
  bgColor: string;
  textColor: string;
  label: string;
} => {
  switch (flowType) {
    case 'CONTACT_FLOW':
      if (resourceType === 'FlowModule') {
        return {
          bgColor: 'rgb(243, 232, 255)',
          textColor: 'rgb(59, 7, 100)',
          label: 'Flow Module'
        };
      }

      return {
        bgColor: 'rgb(219, 234, 254)',
        textColor: 'rgb(23, 37, 84)',
        label: 'Contact Flow'
      };
    case 'QUEUE_CALL_BACK':
      return {
        bgColor: 'rgb(255, 243, 205)',
        textColor: 'rgb(133, 77, 14)',
        label: 'Queue Callback'
      };
    case 'CUSTOMER_QUEUE':
      return {
        bgColor: 'rgb(255, 237, 213)',
        textColor: 'rgb(154, 52, 18)',
        label: 'Customer Queue'
      };
    case 'AGENT_WHISPER':
      return {
        bgColor: 'rgb(248, 250, 252)',
        textColor: 'rgb(2, 8, 23)',
        label: 'Agent Whisper'
      };
    case 'OUTBOUND_WHISPER':
      return {
        bgColor: 'rgb(248, 250, 252)',
        textColor: 'rgb(2, 8, 23)',
        label: 'Outbound Whisper'
      };
    case 'CUSTOMER_WHISPER':
      return {
        bgColor: 'rgb(207, 250, 254)',
        textColor: 'rgb(22, 78, 99)',
        label: 'Customer Whisper'
      };
    default:
      return {
        bgColor: 'rgb(229, 231, 235)',
        textColor: 'rgb(3, 7, 18)',
        label: flowType || 'Unknown'
      };
  }
};

const Badge = ({
  className,
  label,
  badgeStyle
}: {
  className?: string;
  label: string;
  badgeStyle: { bgColor: string; textColor: string; label: string };
}) => (
  <span
    className={`px-2 py-1 text-xs rounded-full font-medium ${className}`}
    style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor }}
  >
    {label}
  </span>
);

export const FlowNode = ({ data }: { data: FlowNodeData }) => {
  const isQueue = data.type?.includes('QUEUE');
  const isModule = data.resourceType === 'FlowModule';
  const duration = data.duration ?? 0;
  const isQueueOverLimit = isQueue && duration > 15;
  const isQueueUnderLimit = isQueue && duration < 5;

  let bgColor = 'rgb(255, 255, 255)';
  let borderColor = 'rgb(148, 163, 184)';
  let textColor = 'rgb(15, 23, 42)';

  if (isModule) {
    bgColor = 'rgb(250, 245, 255)';
    borderColor = 'rgb(196, 181, 253)';
    textColor = 'rgb(88, 28, 135)';
  }

  const badgeStyle = getBadgeStyle(data.resourceType ?? '', data.type ?? '');

  return (
    <div
      className='p-3 rounded-xl border min-w-60 hover:bg-slate-50! transition-all duration-75'
      style={{ backgroundColor: bgColor, borderColor: borderColor }}
    >
      <Handle type='target' position={Position.Left} />
      <div className='flex flex-col gap-1'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between gap-1 -ml-0.5'>
            <Badge label={badgeStyle.label} badgeStyle={badgeStyle} />
            <div
              className='text-sm flex items-center font-mono tabular-nums leading-0 gap-1'
              style={{
                color: isQueueOverLimit
                  ? 'rgb(185, 28, 28)'
                  : isQueueUnderLimit
                    ? 'rgb(21, 128, 61)'
                    : 'rgb(75, 85, 99)'
              }}
            >
              <HourglassIcon className='size-3' />
              {formatDuration(data.duration ?? 0)}
            </div>
          </div>
          <div
            className='text-md font-semibold max-w-[24ch] break-words leading-tight py-1.5'
            style={{ color: textColor }}
          >
            {data.label}
          </div>
        </div>
        {data.resourceType === 'Flow' && data.type === 'CUSTOMER_QUEUE' && isQueueOverLimit && (
          <div
            className='flex items-center gap-1 px-2 py-1.5 rounded-md'
            style={{ backgroundColor: 'rgb(254, 242, 242)', color: 'rgb(185, 28, 28)' }}
          >
            <TriangleAlertIcon className='size-4' style={{ color: 'rgb(185, 28, 28)' }} />
            <span className='text-xs'>High Queue Duration</span>
          </div>
        )}
        {((data.actions && data.actions.length > 0) || (data.flowModuleGroups && data.flowModuleGroups.length > 0)) && (
          <div className='flex flex-col gap-1'>
            {data.actions &&
              data.actions.map((action, idx) => <Action key={idx} action={action} isModule={isModule} />)}

            {data.flowModuleGroups &&
              data.flowModuleGroups.map((moduleGroup, moduleIdx) => (
                <div key={`module-${moduleIdx}`} className='flex flex-col gap-1'>
                  <div
                    className='text-xs px-2 py-1 rounded-md font-medium border flex items-center justify-between gap-2'
                    style={{
                      backgroundColor: 'rgb(243, 232, 255)',
                      color: 'rgb(107, 33, 168)',
                      borderColor: 'rgb(196, 181, 253)'
                    }}
                  >
                    <span>{moduleGroup.moduleName}</span>
                    <div className='flex items-center gap-1 font-mono tabular-nums'>
                      {formatDuration(moduleGroup.duration)}
                    </div>
                  </div>
                  {moduleGroup.actions.map((action, actionIdx) => (
                    <Action key={`${moduleIdx}-${actionIdx}`} action={action} isFlowModule={true} className='ml-2' />
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>
      <Handle type='source' position={Position.Right} />
    </div>
  );
};
